/**
 * The library's button (DESIGN_SYSTEM.md §5.2): `primary`, `secondary`, `danger`, `ghost`, `link` or `outline`, at
 * `sm` or `md`, drawn from the theme's roles with hover, focus, press and disabled states. Every v2 action
 * that is a labelled button is one of these; `ActionButton` stays for the live surfaces that draw its
 * older look (§7.2).
 */
import React from "react";

import type { FocusOutline } from "@rista/ui/library/focusOutline";
import { ButtonBody } from "./ButtonBody";
import { buttonLook } from "./look";
import type { ButtonVariant } from "./look";

export interface ButtonProps {
  label: string;
  onPress: () => void;
  /** `secondary` by default */
  variant?: ButtonVariant;
  /** `md` by default */
  size?: "sm" | "md";
  /** fill the width it is given: an equal half of a row, whose label may wrap to a second line rather than be cut (decision 93) */
  fill?: boolean;
  disabled?: boolean;
  /**
   * Draws as pale as `disabled` but stays enabled: no `aria-disabled`, and a press is reported. For a button whose
   * action needs something still missing, where the press itself names what is missing. Off by default.
   */
  faded?: boolean;
  /** its action is running: progress shown, presses ignored, not drawn disabled */
  busy?: boolean;
  accessibilityLabel?: string;
  /** Keyboard focus arrived or left (`ButtonBody`) */
  onFocus?: () => void;
  onBlur?: () => void;
  /**
   * When the focus outline shows (RESERVATIONS_V2_PLAN.md decision 128): `"always"` (the default) whenever the control is
   * focused, even after a mouse press; `"keyboard"` only when the focus came from the keyboard. The Reservations screens
   * choose `"keyboard"`; every other screen keeps the default.
   */
  focusOutline?: FocusOutline;
  testID?: string;
}

/** The library's labelled button: `ButtonBody`'s behaviour drawn with `buttonLook`. */
export function Button({ variant, size, fill, faded, ...rest }: ButtonProps): React.ReactElement {
  // An equal half cannot grow wider, so its label may wrap to a second line rather than be cut (decision 93).
  return <ButtonBody look={buttonLook} picks={{ variant, size, fill, faded }} labelLines={fill ? 2 : 1} {...rest} />;
}

export type { ButtonVariant };
export default Button;
